import { ICreativeEnvironment } from '@creative/environment';
import { IFontStyle, IFeed, SpanType, ICreativeDataNode } from '@studio/domain';
import { Container } from '@studio/utils/di';
import { injectFontFace, injectFontStyle } from '@studio/utils/dom-utils';
import { _performanceMark } from '@studio/utils/performance';
import { SafeEval } from '@studio/utils/safe-eval';
import { Ad } from '@ad/ad';
import { AdEvents, IAdEventData } from '@ad/domain/ad-events';
import { imageOptimizerDefaults, ImageOptimizerUrlBuilder } from '@ad/utils/image-optimizer';
import { replaceOrigin, isUrl, isRelativeUrl, concatUrl, isBannerflow, getDomain } from '@ad/utils/url';
import { IAnimatedCreative } from './animated-creative.interface';
import { Animator } from './animator';
import { IAnimator } from './animator.header';
import { createCreativeContainer, ICreativeFactory, registerFeaturesByFiles, T } from './creative.container';
import { IFeedStore } from './elements/feed/feed-store.header';
import { FONT_SERVICE_TEXT_PARAMETER, IFontServiceParams, getFontServiceParamsFromString } from './font-service.utils';
import { isImageNode, isTextNode, isVideoNode, isWidgetNode } from './nodes/helpers';
import { CreativeDataNode } from './nodes/creative-data-node';
import { IRenderer } from './renderer.header';


export interface IFontPreloadAsset {
    type: 'font';
    style: IFontStyle;
}

export interface IFeedPreloadAsset {
    type: 'feed';
    id: string;
}

export type PreloadAsset = IFontPreloadAsset | IFeedPreloadAsset;

interface IPreloadAssets {
    fonts: IFontStyle[];
    feeds: string[];
}

export interface IDeeplinkEvent {
    event: MouseEvent;
    deepLinkUrl?: string;
}

export class AnimatedCreative implements IAnimatedCreative {

    env: ICreativeEnvironment;
    hasWidgetElements: boolean;
    animator?: IAnimator;
    feedStore: IFeedStore;
    private _iframe?: HTMLIFrameElement;
    private _rootElement?: HTMLElement;
    private _preloadedFeeds: string[] = [];
    private _preloadAssets: IPreloadAssets = { fonts: [], feeds: [] };
    private _stoppedOmniRenderRecording = false;
    private _inSandboxedIframe = false;

    private get _window(): Window | undefined {
        return (this._inSandboxedIframe ? window : this._iframe?.contentWindow) || undefined;
    }

    private get _document(): Document {
        const document = this._window?.document;
        if (!document) {
            throw new Error('No document found');
        }
        return document;
    }

    /**
     * The creative renderer. Note that this is shared between canvas and creatives.
     */
    renderer?: IRenderer;
    private _pixelVisibleInterval?: number;
    private destroyed = false;
    private creativeStylesRef?: HTMLStyleElement;
    private creativeDataNode: CreativeDataNode;

    constructor(
        public ad: Ad,
        creativeData: ICreativeDataNode,
        preloadAssets: PreloadAsset[],
        private _onLoadCallback?: (creative?: IAnimatedCreative) => any,
        env?: ICreativeEnvironment
    ) {
        console.debug('[AnimatedCreative] construct');

        if (typeof creativeData.nodes === 'undefined' && creativeData.elements.length > 0) {
            creativeData.nodes = creativeData.elements;
        }
        this.creativeDataNode = new CreativeDataNode(creativeData);

        try {
            this._setEnvironments(env);
            for (const preloadAsset of preloadAssets) {
                switch (preloadAsset.type) {
                    case 'font':
                        this._preloadAssets.fonts.push(preloadAsset.style);
                        break;
                    case 'feed':
                        this._preloadAssets.feeds.push(preloadAsset.id);
                        break;
                }
            }

            const iframe = this._createIframe();
            console.debug('[AnimatedCreative] created iframe');

            const contentWindow = iframe.contentWindow;

            this._inSandboxedIframe = !iframe.contentDocument;

            if (this._inSandboxedIframe) {
                console.debug('[AnimatedCreative] is sandboxed. Removing iframe');

                iframe.remove();
                this.init();
            }
            else if (contentWindow) {
                this._iframe = iframe;
                // Wait for iframeWindow to be available
                this._onIframeWindowLoad(contentWindow, this.init);
            }
            else {
                console.warn('Did not initialize the creative, due to container element is not visible:\n', this.ad.container);
                return;
            }
        }
        catch (e) {
            throw new Error('Could not initialize creative: ' + (e as any)?.message);
        }
    }

    /**
     * When iframe window is ready to use, init creative
     */
    private init = async (): Promise<void> => {
        console.debug('[AnimatedCreative] init');
        _performanceMark('ac:init:start');

        const contentWindow = this._window;

        if (!contentWindow) {
            throw new Error('Ad container is not visible');
        }

        if (this.destroyed) {
            return;
        }

        this._insertCreativeStyles();
        this._rootElement = this._getRootElement();

        const adContainer = new Container<T>();
        adContainer.register_m(T.AD, this.ad);
        adContainer.register_m(T.CREATIVE_DATA, this.creativeDataNode);
        adContainer.register_m(T.ENVIRONMENT, this.env);
        const creativeContainer = createCreativeContainer();
        creativeContainer.parent = adContainer;
        const createCreative = creativeContainer.resolve<ICreativeFactory>(T.CREATIVE_FACTORY);

        console.debug('[AnimatedCreative] loading feature files / init IO / SafeEval');
        _performanceMark('ac:init:load:features:start');

        await Promise.all([
            registerFeaturesByFiles(this.ad.selectedCreative.animated.files, this.ad.getOrigin()!, creativeContainer),
            ImageOptimizerUrlBuilder.init_m(
                this.env?.IMAGE_OPTIMIZER_ORIGIN || '',
                this.ad.selectedCreative.animated.defaultOrigin,
                this._getDpr()
            ),
            SafeEval.init(this._inSandboxedIframe)
        ]);

        console.debug('[AnimatedCreative] loaded feature files / init IO / SafeEval');
        _performanceMark('ac:init:load:features:end');

        const creative = createCreative(this.creativeDataNode, {
            contentWindow,
            brandId: this.ad.data.brand.id,
        }, this.ad);

        this.renderer = creative.renderer_m;
        this.renderer.on('click', this._onClick);

        if (creative.feedStore_m) {
            _performanceMark('ac:init:load:feeds:start');

            this.feedStore = creative.feedStore_m;
            this.renderer.feedStore = this.feedStore;

            // Override the first feed, used for example with DCO
            if (this.ad.overriddenFeedData && this._preloadAssets.feeds.length > 0) {
                this.renderer.feedStore.feeds.set(this._preloadAssets.feeds[0], {
                    feed: {
                        data: this.ad.overriddenFeedData,
                        intervalInSeconds: 0,
                    },
                    interval: undefined,
                    overridden: true
                });
            }

            await this._preloadFeeds();
            _performanceMark('ac:init:load:feeds:end');
        }

        this.animator = creative.animator_m;

        this._initEventBubbling();

        _performanceMark('ac:init:render:start');
        // Viewtree has to be initialized before preloading fonts, images and videos
        this.renderer.render(this._rootElement, 0, true);
        _performanceMark('ac:init:render:end');

        try {
            console.debug('[AnimatedCreative] preloading fonts');
            await this._preloadFonts();
            console.debug('[AnimatedCreative] loaded fonts');
        } catch (e) {
            console.error('Failed to preload fonts', e);
        } finally {
            // Note: We have to rerender to include the fonts
            // TODO: Remove this hack in the future. when we rewrite renderer to have loadAssets method.

            _performanceMark('ac:init:rerender:start');
            this.renderer.rerender_m(this.creativeDataNode);
            _performanceMark('ac:init:rerender:end');
        }

        try {
            console.debug('[AnimatedCreative] preloading elements');
            _performanceMark('ac:init:load:elements:start');
            const preloadElements = [...this.renderer.preloadingElements.values()];
            // not supported in all browsers
            if (Promise.allSettled) {
                const results = await Promise.allSettled(preloadElements);
                console.debug('[AnimatedCreative] loaded elements');

                results.forEach(result => {
                    if (result.status === 'rejected') {
                        throw result.reason;
                    }
                });
            } else {
                await Promise.all(preloadElements);
            }
            _performanceMark('ac:init:load:elements:end');
        } catch (err) {
            const error = err as {
                message?: string,
                url?: string
            };
            if (error.message) {
                this._handleError(error.message, {
                    level: 'error',
                    contexts: {
                        image: error.url ? { url: error.url } : undefined,
                        originalError: err
                    }
                });
            } else {
                console.error(err);
            }
        }

        this._checkForWidgetElementNodes();

        if (this.renderer.WidgetRenderer) {
            try {
                console.debug('[AnimatedCreative] loading widgets');
                _performanceMark('ac:init:load:widgets:start');

                // Ugly workaround to ensure that all widgets has completely loaded to ensure that they are in sync with the creative
                await Promise.all(this.renderer.WidgetRenderer.widgetInitializingPromises);
                await Promise.all(this.renderer.WidgetRenderer.widgetLoadingPromises);

                console.debug('[AnimatedCreative] loaded widgets');
                _performanceMark('ac:init:load:widgets:end');
            } catch (err: any) {
                this._handleError(err.message, {
                    level: 'error',
                    contexts: err.contexts ?? undefined
                });
            }
        }

        // Sometimes iframe is hidden (display: none), for on-site banners. Where the publisher have
        // implemented media queries to show and hide multiple banners to simulate responsiveness.
        // Hidden loaded banners have the fallback, that they have zero height when inspected, which
        // currently affects text rendering. So, we have to wait until it is shown to rerender.
        _performanceMark('ac:init:visibility:check');

        if (!this._isPixelVisible()) {
            console.debug('[AnimatedCreative] not visible. Adding visibility listener');

            this._pixelVisibleInterval = contentWindow.setInterval(() => {
                if (this._isPixelVisible()) {
                    console.debug('[AnimatedCreative] became visible');

                    this.renderer?.rerender_m(this.creativeDataNode);
                    this._showIframe();
                    if (Animator) {
                        this._initPlayback();
                    }
                    contentWindow.clearInterval(this._pixelVisibleInterval);
                }
            }, 10);
        }
        else {
            this._showIframe();

            if (Animator) {
                this._initPlayback();
            }
        }

        if (typeof this._onLoadCallback === 'function') {
            this._onLoadCallback(this);
        }

        _performanceMark('ac:init:end');
    };

    private _getRootElement(): HTMLElement {
        if (this._inSandboxedIframe) {
            return this.ad.container;
        }
        else {
            return this._document.body;
        }
    }

    /**
     * @summary
     * Check pixel visibilty.
     *
     * @description
     * The ability to render visible pixels on the banner. Notice, this API is different from
     * DOM visibility, where display none is still counted as visibly. We have manually confirmed
     * that display none:ed DOM subtree's are cheap to check bounding rect.
     */
    private _isPixelVisible(): boolean {
        const testElement = this._document.createElement('div');
        const style = testElement.style;
        style.height = '1px';
        style.width = '1px';
        style.position = 'absolute';
        style.top = '-100000px';
        this.ad.container.appendChild(testElement);
        const result = testElement.getBoundingClientRect().height > 0;
        this.ad.container.removeChild(testElement);
        return result;
    }

    private _checkForWidgetElementNodes(): void {
        if (!this.renderer) {
            return;
        }

        for (const element of this.renderer.creativeDocument.elements) {
            if (isWidgetNode(element)) {
                this.hasWidgetElements = true;
                break;
            }
        }
    }

    private _initPlayback(): void {
        _performanceMark('ac:animator:start');

        if (!this.animator) {
            return;
        }

        const autoplay = this.ad.shouldAutoplay();
        this.animator.seek(this._getStartTime());

        if (this.ad.isOmniRender()) {
            // Map stop and pause for omniRender record logic
            // OmniRender stops recording when loop is emitted
            // and set loops to 1
            this.creativeDataNode.loops = 1;
            this.animator.on('pause', () => {
                if (!this._stoppedOmniRenderRecording) {
                    this._stoppedOmniRenderRecording = true;
                    setTimeout(() => this.animator?.emit('loop'), 0);
                }
            });
        }
        // Play/pause creative when it leaves or enter view
        if (this.ad.viewability.pauseOutsideView()) {
            this.ad.on(AdEvents.ViewEnter, () => {
                this.animator?.play();
            });
            this.ad.on(AdEvents.ViewLeave, () => {
                this.animator?.pause();
            });
            if (this.ad.viewability.visible && autoplay) {
                this.animator.play();
            }
        }
        else if (autoplay) {
            console.debug('[AnimatedCreative] autoplaying');
            this.animator.play();
        }

        this.ad.on(AdEvents.Stop, () => {
            this.animator?.pause();
        });

        _performanceMark('ac:animator:end');
    }

    /**
     * When user clicks the creative
     */
    private _onClick = (e: MouseEvent, deepLinkUrl?: string): void => {
        const url = this._getClickUrl();
        if (!url) {
            return;
        }

        if (deepLinkUrl) {
            this.ad.tracking.click({ x: e.x, y: e.y, deepLinkUrl });
        }
        else {
            this.ad.tracking.click(e);
        }
    };

    private _getStartTime(): number {
        const seek = parseFloat(this.ad.parameters?.seek || '');

        // Seek parameter is added to the tag
        if (!isNaN(seek)) {
            return seek;
        }
        // Used by image generator to capture preload image at the right place in timeline
        if (this._shouldStartFromPreloadFrame()) {
            const preloadFrame = this.creativeDataNode.preloadImage?.frames && this.creativeDataNode.preloadImage.frames[0];

            // Defaults to 1 if not set
            return typeof preloadFrame === 'number' ? preloadFrame : 1;
        }
        else {
            return this.creativeDataNode.startTime || 0;
        }
    }

    private _shouldStartFromPreloadFrame(): boolean {
        return this.ad.parameters?.seek === 'preload-frame';
    }

    private _setEnvironments(env?: ICreativeEnvironment): void {
        if (env) {
            this.env = env;
            this.env.ANIMATED_CREATIVE_JS = true;
            this.env.IN_IMAGE_GENERATOR = this.ad.parameters.env === 'image-generator' || this._shouldStartFromPreloadFrame();
            if (!isBannerflow(window.location.href)) {
                const origin = this.ad.getOrigin();
                if (!isBannerflow(origin!)) {
                    this.env.CUSTOM_AD_DOMAIN = getDomain(origin, false);
                }

                if (origin) {
                    for (const e in this.env) {
                        const value = this.env[e];
                        if (!isUrl(value)) {
                            continue;
                        }
                        if (isRelativeUrl(value)) {
                            this.env[e] = concatUrl(origin, value);
                        } else if (!origin.includes('bannerflow.com/creatives')) {
                            // don't replace origin for ACG hosted ads in production
                            this.env[e] = replaceOrigin(value, origin);
                        }
                    }
                }
            }
            imageOptimizerDefaults.imageOptimizerOrigin = this.env.IMAGE_OPTIMIZER_ORIGIN;
        }
    }

    /**
     * Create wrapping iframe.
     * Note that in this stage we cannot insert content into the iframe in all browers (FF)
     */
    private _createIframe(): HTMLIFrameElement {
        const iframe = document.createElement('iframe');
        // Dimension hacks to fix iframe issues in iOS:
        // https://stackoverflow.com/a/20142280
        const style = iframe.style;
        style.width = style.height = '1px';
        style.minWidth = style.maxWidth = style.minHeight = '100%';
        style.outline = style.border = style.transform = style.filter = 'none';
        style.display = 'block';
        style.position = 'absolute';
        style.left = style.top = style.opacity = '0';
        style.transformOrigin = '0 0';
        iframe.src = 'about:blank';
        iframe.scrolling = 'no';

        if (this.ad.shouldFadeIn()) {
            style.transition = 'opacity 0.2s ease';
        }
        this.ad.container.appendChild(iframe);

        return iframe;
    }

    private _showIframe(): void {
        console.debug('[AnimatedCreative] showing iframe');
        _performanceMark('ac:iframe:show');

        if (this._iframe) {
            this._iframe.style.opacity = '1';
        }
    }
    /**
     * Needs to be executed AFTER the document is ready
     */
    private _insertCreativeStyles(): void {

        const url = this._getClickUrl();
        const cursor = url ? 'pointer;' : 'default;';
        const doc = this._document;
        const style = doc.createElement('style');

        style.innerHTML = `*{box-sizing: border-box;}html,body{margin:0;padding:0;overflow:hidden;width:100%;height:100%;-ms-user-select:none;-moz-user-select:none;-webkit-user-select:none;user-select:none;cursor:${cursor};}`;

        this.creativeStylesRef = style;

        doc.head.appendChild(style);
    }

    private _getClickUrl(): string | undefined {

        const redirectUrl = this.ad.tracking.getRedirectUrl();

        if (redirectUrl === false) {
            return;
        }

        return redirectUrl || this.ad.tracking.getTargetUrl();
    }

    /**
     * When iframe window is ready
     * @param callback
     */
    private _onIframeWindowLoad(iframeWindow: Window, callback: () => any): void {
        console.debug('[AnimatedCreative] waiting for iframe');

        if (iframeWindow.document.readyState === 'complete') {
            setTimeout(() => {
                callback();
            }, 100);
        } else {
            const onLoad = (): void => {
                if (callback) {
                    callback();
                }
                if (iframeWindow) {
                    iframeWindow.removeEventListener('load', onLoad);
                }
            };
            iframeWindow.addEventListener('load', onLoad);
        }
    }

    private _initEventBubbling(): void {
        const { animator, ad } = this;

        if (animator && ad) {
            console.debug('[AnimatedCreative] subscribing to animator events');

            animator.on('loop', () => ad.emit(AdEvents.Loop, [this._getAnimatorEventData()]));
            animator.on('pause', () => ad.emit(AdEvents.Pause, [this._getAnimatorEventData()]));
            animator.on('play', () => ad.emit(AdEvents.Play, [this._getAnimatorEventData()]));
            animator.on('stop', () => ad.emit(AdEvents.Stop, [this._getAnimatorEventData()]));
            animator.on('seek', () => ad.emit(AdEvents.Seek, [this._getAnimatorEventData()]));
            _performanceMark('ac:animated-events:added');
        }
    }

    private _getAnimatorEventData(): IAdEventData {
        const animator = this.animator;

        return {
            ad: this.ad,
            time: animator?.time || 0,
            loop: animator?.loop || 0
        };
    }

    destroy(): void {
        clearInterval(this._pixelVisibleInterval);
        this._rootElement?.removeEventListener('click', this._onClick);
        this.destroyed = true;

        try {
            if (this.creativeStylesRef) {
                this._document.head.removeChild(this.creativeStylesRef);
                this.creativeStylesRef = undefined;
            }
            // eslint-disable-next-line no-empty
        } catch { }

        this.renderer?.destroy();
        this.animator?.destroy();
    }

    /**
     * Preloads all feeds as well as fonts that are used in conjunction as character styling
     */
    private async _preloadFeeds(): Promise<void> {
        _performanceMark('ac:preload:feeds:start');

        this.creativeDataNode.elements.forEach(child => {
            if (isTextNode(child)) {
                for (const span of child.content.spans) {
                    if (span.type === SpanType.Variable) {
                        if (this._preloadedFeeds.indexOf(span.style.variable!.id) === -1) {
                            this._preloadedFeeds.push(span.style.variable!.id);
                        }
                    }
                }
            }
            else if (child.feed && (isImageNode(child) || isVideoNode(child))) {
                    if (this._preloadedFeeds.indexOf(child.feed.id) === -1) {
                        this._preloadedFeeds.push(child.feed.id);
                    }
            }
        });

        await this.feedStore.preloadFeeds(this._preloadedFeeds);
        _performanceMark('ac:preload:feeds:end');
    }

    private _getDpr(): number {
        // STUDIO-6228 - use better quality images in exported creatives
        // explicitly left out for fitOption=cover above, because it needs the exact size
        if (this.env.IN_IMAGE_GENERATOR || this.ad.isOmniRender()) {
            return 2;
        }
        return 1;
    }

    private async _preloadFonts(): Promise<void> {
        _performanceMark('ac:preload:fonts:start');

        if (this.feedStore) {
            this.renderer?.creativeDocument.elements.forEach(child => {
                if (isTextNode(child)) {
                    for (const span of child.content.spans) {
                        if (span.type === SpanType.Variable) {
                            this.feedStore.addFeedElement(child.id, span.style.variable as IFeed, child);
                        }
                    }
                }
            });

            for (const id of this._preloadedFeeds) {
                // Adds to fontCharacterCache and uses preload below if in output
                this.feedStore.loadFontsBasedOnFeedData(id, true);
            }
        }

        for (const font of this._preloadAssets.fonts) {
            if (font) {
                if (!font.src.startsWith('data')) {
                    const [url, encodedChars] = font.src.split(FONT_SERVICE_TEXT_PARAMETER);
                    const feedCharacters = (this.feedStore && this.feedStore.fontCharacterCache.get(font.id)) || '';
                    const textCharacters = decodeURIComponent(encodedChars);
                    const { textParams, distinctText }: IFontServiceParams = getFontServiceParamsFromString(feedCharacters + textCharacters);
                    if (feedCharacters) {
                        this.feedStore.fontCharacterCache.set(font.id, distinctText);
                        font.src = url + textParams;
                    }
                    // Don't load the font if no characters are included
                    if (!distinctText) {
                        continue;
                    }
                }

                try {
                    const fallbackFontInjection = window.app === 'bannerflow' && this.ad.parameters['font-loading'];
                    if (!fallbackFontInjection) {
                        const fontFace = await injectFontFace(font, this.env.FONTSERVICE_API_ORIGIN);
                        this._document.fonts.add(fontFace);
                    } else {
                        injectFontStyle(this._document, font, this.env.FONTSERVICE_API_ORIGIN);
                        await this._document.fonts.load(`1em f-${font.id}`); // resolves once the passed font family is loaded
                    }
                } catch (err) {
                    /* Do nothing */
                    this._handleError('Could not load font', {
                        level: 'error',
                        contexts: {
                            font,
                            originalError: err
                        }
                    });
                }
            }
        }
        _performanceMark('ac:preload:fonts:end');
    }

    private _handleError(message: string, sentry?: SentryError): void {
        if (sentry && window.app === 'bannerflow' && !!Sentry) {
            Sentry.captureMessage(message, {
                level: sentry.level,
                contexts: sentry.contexts
            });
        }
        console.error(message);
    }
}

interface SentryError {
    level: string;
    contexts?: { [key: string]: any; };
}

export { Color } from '../creative/color';
